import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, TrendingUp, TrendingDown, Minus, LineChart, BarChart3, Trophy, Zap, RefreshCw, ArrowUpRight, ArrowDownRight } from "lucide-react";

const StrategicCtx = createContext({ data: null, loading: true, reload: () => {} });

export function PlatformStrategicProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/platform/strategic");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return <StrategicCtx.Provider value={{ data, loading, reload: load }}>{children}</StrategicCtx.Provider>;
}

const trendIcon = (t) => t === "up" ? { I: TrendingUp, cls: "text-emerald-600" } : t === "down" ? { I: TrendingDown, cls: "text-rose-600" } : { I: Minus, cls: "text-slate-500" };

// ═══════════════════════════════════════════════════════════════════════════
// Growth Trend tile
// ═══════════════════════════════════════════════════════════════════════════
export function GrowthTrend() {
  const { data, loading, reload } = useContext(StrategicCtx);
  if (loading) return (<Card className="p-4 border-slate-200" data-testid="growth-trend-loading"><Loader2 className="h-4 w-4 animate-spin text-slate-500"/></Card>);
  if (!data || data.forbidden || data.error) return null;
  const g = data.growth || {};
  const { I: Ti, cls } = trendIcon(g.trend);
  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="growth-trend">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <LineChart className="h-4 w-4 text-indigo-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">Growth Trend</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={reload} className="h-7 px-2" data-testid="growth-trend-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">New orgs · last 30d</div>
      <div className="flex items-end gap-2">
        <div className="font-heading font-black text-3xl text-slate-900" data-testid="growth-trend-value">{g.last30 || 0}</div>
        <div className={`flex items-center gap-0.5 text-sm font-semibold ${cls} pb-1`}>
          <Ti className="h-4 w-4"/>
          {g.changePct > 0 ? "+" : ""}{g.changePct}%
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">vs prev 30d ({g.prev30 || 0})</div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Usage Trend tile
// ═══════════════════════════════════════════════════════════════════════════
export function UsageTrend() {
  const { data, loading } = useContext(StrategicCtx);
  if (loading) return (<Card className="p-4 border-slate-200" data-testid="usage-trend-loading"><Loader2 className="h-4 w-4 animate-spin text-slate-500"/></Card>);
  if (!data || data.forbidden || data.error) return null;
  const u = data.usage || {};
  const { I: Ti, cls } = trendIcon(u.trend);
  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="usage-trend">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="h-4 w-4 text-indigo-600"/>
        <h3 className="font-semibold text-slate-900 text-sm">Usage Trend</h3>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Signups · last 30d</div>
      <div className="flex items-end gap-2">
        <div className="font-heading font-black text-3xl text-slate-900" data-testid="usage-trend-value">{(u.signupsLast30 || 0).toLocaleString("en-GB")}</div>
        <div className={`flex items-center gap-0.5 text-sm font-semibold ${cls} pb-1`}>
          <Ti className="h-4 w-4"/>
          {u.changePct > 0 ? "+" : ""}{u.changePct}%
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">vs prev 30d ({(u.signupsPrev30 || 0).toLocaleString("en-GB")})</div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Top Performing Clients tile (with ▲ movement)
// ═══════════════════════════════════════════════════════════════════════════
export function TopPerformingClients() {
  const { data, loading } = useContext(StrategicCtx);
  if (loading) return (<Card className="p-4 border-slate-200" data-testid="top-performing-loading"><Loader2 className="h-4 w-4 animate-spin text-slate-500"/></Card>);
  if (!data || data.forbidden || data.error) return null;
  const rows = (data.topPerforming || []).filter(r => r.signups30d > 0).slice(0, 5);
  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="top-performing-clients">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="h-4 w-4 text-emerald-600"/>
        <h3 className="font-semibold text-slate-900 text-sm">Top Performing</h3>
        <span className="text-xs text-slate-500">by signups · 30d</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-2 text-center">No signup activity across fleet.</div>
      ) : (
        <div className="space-y-1" data-testid="top-performing-list">
          {rows.map((r, i) => (
            <div key={r.organisationId} className="flex items-center gap-2 p-1.5 rounded-md" data-testid={`top-performing-row-${r.organisationId}`}>
              <span className="text-[10px] font-mono text-slate-400 w-4">#{i + 1}</span>
              <span className="text-sm text-slate-900 font-medium truncate flex-1">{r.organisation}</span>
              <span className="text-xs font-mono text-slate-700">{r.signups30d}</span>
              {r.direction === "up" && <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 border text-[10px] px-1 py-0"><ArrowUpRight className="h-3 w-3"/>{r.deltaPct > 0 ? `+${r.deltaPct}%` : ""}</Badge>}
              {r.direction === "down" && <Badge className="bg-rose-50 text-rose-800 border-rose-200 border text-[10px] px-1 py-0"><ArrowDownRight className="h-3 w-3"/>{r.deltaPct}%</Badge>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Underused Clients tile
// ═══════════════════════════════════════════════════════════════════════════
export function UnderusedClients() {
  const { data, loading } = useContext(StrategicCtx);
  if (loading) return (<Card className="p-4 border-slate-200" data-testid="underused-loading"><Loader2 className="h-4 w-4 animate-spin text-slate-500"/></Card>);
  if (!data || data.forbidden || data.error) return null;
  const rows = (data.underused || []).slice(0, 5);
  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="underused-clients">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="h-4 w-4 text-amber-600"/>
        <h3 className="font-semibold text-slate-900 text-sm">Underused</h3>
        <span className="text-xs text-slate-500">{rows.length} at-risk</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-2 text-center" data-testid="underused-empty">All clients at healthy usage levels.</div>
      ) : (
        <div className="space-y-1" data-testid="underused-list">
          {rows.map((r) => (
            <div key={r.organisationId} className="flex items-center gap-2 p-1.5 rounded-md" data-testid={`underused-row-${r.organisationId}`}>
              <span className="text-sm text-slate-900 font-medium truncate flex-1">{r.organisation}</span>
              <span className="text-[10px] text-slate-500">{r.usersCount}u</span>
              <span className="text-xs font-mono text-amber-700">{r.signups30d}s/30d</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Client Movement Indicators strip (▲ improving / ▼ slipping)
// ═══════════════════════════════════════════════════════════════════════════
export function ClientMovementStrip() {
  const { data, loading } = useContext(StrategicCtx);
  if (loading) return null;
  if (!data || data.forbidden || data.error) return null;
  const up = data.movement?.improving || [];
  const down = data.movement?.slipping || [];
  if (up.length === 0 && down.length === 0) return null;
  return (
    <Card className="p-3 border-slate-200 bg-white" data-testid="client-movement-strip">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Client Movement</div>
        {up.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="movement-improving">
            <ArrowUpRight className="h-4 w-4 text-emerald-600"/>
            {up.map(m => (
              <Badge key={m.organisationId} className="bg-emerald-50 text-emerald-800 border-emerald-200 border text-[11px] px-2 py-0">
                {m.organisation} +{m.deltaPct}%
              </Badge>
            ))}
          </div>
        )}
        {down.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="movement-slipping">
            <ArrowDownRight className="h-4 w-4 text-rose-600"/>
            {down.map(m => (
              <Badge key={m.organisationId} className="bg-rose-50 text-rose-800 border-rose-200 border text-[11px] px-2 py-0">
                {m.organisation} {m.deltaPct}%
              </Badge>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
